import clsx from 'clsx';
import { Description, Field, Label, Select as HeadlessSelect } from '@headlessui/react';
import { useField, useFormikContext } from 'formik';
import { SELECT_INPUT_TEST_IDS } from './constants';
import InputError from '~/components/feedback/InputError';
import type { IDropdownOption, ISelectInput } from '~/types/inputs';
import { getInputSizeClasses, isOptionOptionGroup } from '~/utils/form';
import config from '~/config';
import './style.css';

const DefaultOption = ({ option }: { option: IDropdownOption }) => {
	return (
		<option value={option.value} key={option.value}>
			{option.label}
		</option>
	);
};

const OptGroupOptions = ({ options, label }: { options: IDropdownOption[]; label: string }) => {
	return (
		<optgroup label={label}>
			{options.map((option, index) => {
				return <DefaultOption option={option} key={index} />;
			})}
		</optgroup>
	);
};

export default function Select({
	name,
	label,
	width,
	required,
	options,
	description,
}: ISelectInput) {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	return (
		<Field
			className={clsx(
				'select-input-container mx-auto rounded-tl-input rounded-tr-input relative h-input w-full',
				getInputSizeClasses(width),
			)}
			data-testid={SELECT_INPUT_TEST_IDS.ROOT}
		>
			<HeadlessSelect
				{...field}
				className={clsx(
					'select-input-input peer cursor-pointer peer appearance-none bg-transparent placeholder-transparent text-base w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full',
					field.value ? 'has-value' : '',
					isError ? 'is-error' : '',
				)}
				id={name}
				data-testid={SELECT_INPUT_TEST_IDS.INPUT}
			>
				{options.map((option, index) => {
					if (isOptionOptionGroup(option)) {
						return (
							<OptGroupOptions
								options={option.options}
								label={option.groupLabel}
								key={index}
							/>
						);
					}

					return <DefaultOption option={option} key={index} />;
				})}
			</HeadlessSelect>
			<Label
				htmlFor={name}
				className={clsx(
					'select-input-label pointer-events-none transition-all top-3 text-xs absolute left-5',
					field.value ? 'has-value' : '',
				)}
				data-testid={SELECT_INPUT_TEST_IDS.LABEL}
			>
				{label}
				{required && !label.endsWith('*') ? <sup>*</sup> : null}
			</Label>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="15.338"
				height="9.79"
				viewBox="0 0 15.338 9.79"
				className="select-input-chevron absolute top-1/2 right-5 pointer-events-none"
			>
				<path
					data-name="Path 2662"
					d="m2.121 2.121 5.548 5.548 5.548-5.548"
					fill="none"
					stroke="currentColor"
					strokeLinecap={config.areElementsRounded ? 'round' : 'butt'}
					strokeWidth="3"
					className="transition-colors duration-200"
				/>
			</svg>
			<div className="select-input-border absolute -bottom-[2px] left-0 transition-colors duration-200 w-full h-[2px]" />
			{description && (
				<Description className="text-xs text-left mt-1">{description}</Description>
			)}
			<InputError name={name} isError={isError}>
				{meta.error}
			</InputError>
		</Field>
	);
}
